import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useInspections, useInspectionTemplates } from '@/hooks/useInspections';
import { useSites } from '@/hooks/useSites';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/constants/roles';
import { format } from 'date-fns';
import { 
  Search, 
  Filter, 
  Eye, 
  Play, 
  Building2, 
  User, 
  Calendar,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText
} from 'lucide-react';

interface InspectionHistoryProps {
  onViewReport: (inspectionId: string) => void;
  onResumeInspection: (inspectionId: string) => void;
}

export const InspectionHistory: React.FC<InspectionHistoryProps> = ({
  onViewReport,
  onResumeInspection
}) => {
  const { user, userRole } = useAuth();
  const canViewAll = isAdmin(userRole as any);
  
  const [filters, setFilters] = useState({
    status: '',
    site_id: '',
    template_id: '',
    search: ''
  });

  const { data: inspections = [], isLoading, error, isError } = useInspections({
    status: filters.status || undefined,
    site_id: filters.site_id && filters.site_id !== 'all' ? filters.site_id : undefined,
    template_id: filters.template_id && filters.template_id !== 'all' ? filters.template_id : undefined
  });

  const { data: templates = [] } = useInspectionTemplates();
  const { sites = [] } = useSites();

  // Log error if any
  if (isError) {
    console.error('[InspectionHistory] Error loading inspections:', error);
  }

  // Filter by search and user access
  const filteredInspections = inspections.filter(inspection => {
    // Non-admins only see their own inspections
    if (!canViewAll && inspection.inspector_id !== user?.id) {
      return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesTemplate = inspection.template?.name?.toLowerCase().includes(searchLower);
      const matchesSite = inspection.site?.name?.toLowerCase().includes(searchLower);
      const matchesArea = inspection.area?.toLowerCase().includes(searchLower);
      if (!matchesTemplate && !matchesSite && !matchesArea) {
        return false;
      }
    }
    
    return true;
  });

  const getStatusBadge = (status: string) => {
    if (status === 'submitted') {
      return (
        <Badge variant="default" className="bg-success text-success-foreground">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Submitted
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Clock className="h-3 w-3 mr-1" />
        Draft
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-destructive">
            <AlertTriangle className="h-12 w-12 mx-auto mb-3" />
            <p className="font-medium">Error loading inspections</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search inspections..."
                  className="pl-10"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>

            <Select 
              value={filters.status || "all"}
              onValueChange={(value) =>
                setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.site_id} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, site_id: value }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Facilities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Facilities</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.template_id} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, template_id: value }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Templates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Inspection History</CardTitle>
          <CardDescription>
            {filteredInspections.length} inspection(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredInspections.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No inspections found</p>
              <p className="text-sm">Adjust your filters or start a new inspection</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Facility</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInspections.map((inspection) => (
                    <TableRow key={inspection.id}>
                      <TableCell className="font-medium">
                        {inspection.template?.name || 'Unknown Template'}
                      </TableCell>
                      <TableCell>
                        {inspection.site ? (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {inspection.site.name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {inspection.area || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(inspection.started_at), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(inspection.started_at), 'h:mm a')}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(inspection.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        {inspection.status === 'draft' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onResumeInspection(inspection.id)}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Resume
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onViewReport(inspection.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
